import { Box, FormControl, InputLabel, Link, MenuItem, Select, SelectChangeEvent, Stack, TextField, Typography } from "@mui/material";
import { INicu } from "../types/interfaces/INicu";
import { IDropdownList } from "../types/interfaces/IDropdownList";
import { SearchParms } from "../types/classes/SearchParms";
import { DisplayParms } from "../types/classes/DisplayParms";

const NAME = "Name";
const CITY = "City";
const STATE = "State";
const ZIP_CODE = "ZipCode";
const LEVEL_ASSIGNED_BY = "LevelAssignedby";
const LEVEL = "Level";
const NUMBER_OF_BEDS = "NumberofBeds";

//This functions gets a list by name used for a dropdownlist.
function GetListFromNicus(name: string, nicus: Array<INicu>): IDropdownList{
    let list: string[] = [];
    let tempList: string[] = [];
    let displayName: string = "";

    switch(name) {
        case CITY:
            tempList = nicus.map(nicu => nicu.City?.trim().toUpperCase()).flatMap(f => f ? [f] : []);
            displayName = name;
            break;
        case STATE:
            tempList = nicus.map(nicu => nicu.State?.trim().toUpperCase()).flatMap(f => f ? [f] : []);
            displayName = name;
            break;
        case ZIP_CODE:
            tempList = nicus.map(nicu => nicu.Zip?.indexOf('-') == -1 ? nicu.Zip?.trim() : nicu.Zip?.substring(0, nicu.Zip?.indexOf('-')).trim()).flatMap(f => f ? [f] : []);
            displayName = "Zip Code";
            break;
        case LEVEL_ASSIGNED_BY:
            tempList = nicus.map(nicu => nicu.LevelAssignerName?.trim().toUpperCase()).flatMap(f => f ? [f] : []);
            displayName = "Level Assigned by";
            break;
        default:
            break;
    }
    //remove duplicates and sort in alphabetical order
    list = tempList.filter((c, idx) => tempList.indexOf(c) === idx).sort((a, b) => (a < b ? -1 : 1));

    return {name, displayName, list};
}

//This function gets a copy of SearchParms object.
function GetCopyOfSearchParms(searchParms: SearchParms) : SearchParms {
    
    const newSearchParms = new SearchParms();
    newSearchParms[NAME] = searchParms[NAME];
    newSearchParms[CITY] = searchParms[CITY];
    newSearchParms[STATE] = searchParms[STATE];
    newSearchParms[ZIP_CODE] = searchParms[ZIP_CODE];
    newSearchParms[LEVEL] = searchParms[LEVEL];
    newSearchParms[LEVEL_ASSIGNED_BY] = searchParms[LEVEL_ASSIGNED_BY];
    newSearchParms[NUMBER_OF_BEDS] = searchParms[NUMBER_OF_BEDS];
    return newSearchParms;
}

//This function dynamically creates a dropdownlist with a list of values and search parameter.
function DropdownList(info: {parms: IDropdownList, searchParms: SearchParms, onChange: (parms: SearchParms) => void, onDisplayChange: (displayParms: DisplayParms) => void}) {
    
    let propertyName = info.parms.name as keyof SearchParms;

    const handleChange = (event: SelectChangeEvent) => {
        const newSearchParms = GetCopyOfSearchParms(info.searchParms);
        newSearchParms[propertyName] = event.target.value;
        info.onChange(newSearchParms);
        info.onDisplayChange(new DisplayParms());
      };
      
    return ( 
        <FormControl size="small" sx={{ m: 1, width: '272px' }}>
            <InputLabel id={`nicu-input-label-${info.parms.name}`}>{info.parms.displayName}</InputLabel>
            <Select
            labelId={info.parms.name}
            id={`nicu-select-${info.parms.name}`}
            value={info.searchParms[propertyName]}
            name={info.parms.name}
            label={info.parms.displayName} 
            onChange={handleChange}
            >
                <MenuItem value="" key={`nicu-menu-item-default-${info.parms.name}`}>
                    <em>Select</em>
                </MenuItem>
                {info.parms.list.map((item, index) => (<MenuItem value={item} key={`nicu-menu-item-${info.parms.name}-${index}`}>{item}</MenuItem>))}
            </Select>
        </FormControl>
    );
}

//This function creates filters for nicus.
export default function NicuFilter(nicuList: {nicus: Array<INicu>, searchParms: SearchParms, onSearchParmsChange: (parms: SearchParms) => void, onDisplayChange: (displayParms: DisplayParms) => void}) {
    let city: IDropdownList = GetListFromNicus(CITY, nicuList.nicus);
    let state: IDropdownList = GetListFromNicus(STATE, nicuList.nicus);
    let zipCode: IDropdownList = GetListFromNicus(ZIP_CODE, nicuList.nicus);
    let levelAssignedBy: IDropdownList = GetListFromNicus(LEVEL_ASSIGNED_BY, nicuList.nicus);

    let level: IDropdownList = {
        name: LEVEL,
        displayName: "Level",
        list: ["1", "2", "3", "4"]
    }

    let numOfBeds: IDropdownList = {
        name: NUMBER_OF_BEDS, 
        displayName: "Number of Beds", 
        list: ["Less Than 20", "20 to 29", "30 to 39", "40 to 49", "50 to 59", "60 to 69", "70 to 79", "80 or More"]
    };

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newSearchParms = GetCopyOfSearchParms(nicuList.searchParms);
        newSearchParms.Name = event.target.value;
        nicuList.onSearchParmsChange(newSearchParms)
        nicuList.onDisplayChange(new DisplayParms());
    };

    const handleResetFilters = () => {
        const searchParms = new SearchParms();
        nicuList.onSearchParmsChange(searchParms)
        let newDisplayParms = new DisplayParms();
        newDisplayParms.ResetShowFlag = true;
        nicuList.onDisplayChange(newDisplayParms);
    };

    return (
        <Stack spacing={2}>
            <Box>
                <Stack spacing={20} direction="row">
                    <Typography variant="h4" color="primary.main" component="div">Filter</Typography>
                    <Link color="secondary.dark" sx={{fontSize: 18}} component="button" onClick={handleResetFilters} >Reset</Link>
                </Stack>
            </Box>
            <TextField size="small" id={NAME} label={NAME} type="search" value={nicuList.searchParms.Name} onChange={handleNameChange} sx={{m: 1, width: '272px'}} />
            <DropdownList parms={city} searchParms={nicuList.searchParms} onChange={nicuList.onSearchParmsChange} onDisplayChange={nicuList.onDisplayChange} />
            <DropdownList parms={state} searchParms={nicuList.searchParms} onChange={nicuList.onSearchParmsChange} onDisplayChange={nicuList.onDisplayChange} />
            <DropdownList parms={zipCode} searchParms={nicuList.searchParms} onChange={nicuList.onSearchParmsChange} onDisplayChange={nicuList.onDisplayChange} />
            <DropdownList parms={level} searchParms={nicuList.searchParms} onChange={nicuList.onSearchParmsChange} onDisplayChange={nicuList.onDisplayChange} />
            <DropdownList parms={levelAssignedBy} searchParms={nicuList.searchParms} onChange={nicuList.onSearchParmsChange} onDisplayChange={nicuList.onDisplayChange} />
            <DropdownList parms={numOfBeds} searchParms={nicuList.searchParms} onChange={nicuList.onSearchParmsChange} onDisplayChange={nicuList.onDisplayChange} />
        </Stack>
    );
}